import Button from "@/components/Button"
import { MODE_HEADPHONES, MODE_RCA, DEVICE_CONNECTED } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { useSerialDevice } from "@/contexts/serial-device"
import { ReactComponent as HeadphoneIcon } from "@/icons/headphone-icon.svg"
import { ReactComponent as RCAIcon } from "@/icons/rca-icon.svg"
import { ReactComponent as StarIcon } from "@/icons/star-icon.svg"
import { ReactComponent as USBIcon } from "@/icons/usb-icon.svg"
import { ReactComponent as TOSLINKIcon } from "@/icons/toslink-icon.svg"
import { ReactComponent as TrashIcon } from "@/icons/trash-icon.svg"
import { ReactComponent as CirclePlusIcon } from "@/icons/circle-plus-icon.svg"
import { ReactComponent as SearchIcon } from "@/icons/search-icon.svg"
import { ReactComponent as CloseIcon } from "@/icons/close-icon.svg"
import WarningCallout from "@/components/WarningCallout"
import PresetOptionsMenu from "@/components/PresetOptionsMenu"
import cx from "classnames"
import { useNavigate } from "react-router-dom"
import { useState, useEffect, useRef } from "react"
import { toast } from "react-toastify"
import { ReactComponent as ElementIcon } from "@/icons/element-icon.svg"
import SaveToDeviceModal from "@/components/modals/SaveToDeviceModal"
import SharedPresetsModal from "@/components/modals/SharedPresetsModal"
import { ReactComponent as SortIcon } from "@/icons/sort-icon.svg"

const ModePanel = ({
  mode,
  presets,
  activePreset,
  onModeChanged,
  onPresetSelected,
  onDeletePresetClicked,
  onRenamePresetClicked,
  onSearchTextChanged,
  deviceSettings,
  deviceProfiles,
  activeDeviceProfile,
  onSaveToDevice,
  onDeleteDeviceProfile,
  onActivateDeviceProfile,
  deviceSupportsProfiles,
  activePresetOutdated,
  isPresetLoading,
  onSavePresetClicked,
  createPreset,
}) => {
  const { isAuthenticated } = useAuth()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isAddingPreset, setIsAddingPreset] = useState(false)
  const [newPresetName, setNewPresetName] = useState("")
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState("")
  const [selectedPreset, setSelectedPreset] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [isSharedPresetsModalOpen, setIsSharedPresetsModalOpen] = useState(false)

  return (
    <>
      <ModePanelHeader mode={mode} onModeChanged={onModeChanged} deviceSettings={deviceSettings} />
      <section className="mt-3.5">
        {deviceSupportsProfiles && (
          <DevicePresetsPanel
            onPresetSelected={onPresetSelected}
            presets={presets}
            currentEqState={deviceSettings}
            deviceProfiles={deviceProfiles}
            activeDeviceProfile={activeDeviceProfile}
            onSaveToDevice={onSaveToDevice}
            onDeleteDeviceProfile={onDeleteDeviceProfile}
            onActivateDeviceProfile={onActivateDeviceProfile}
            mode={mode}
            activePreset={activePreset}
          />
        )}
        {isAuthenticated() ? (
          <div className="mb-6">
            <h3 className="type-heading-xs flex items-center gap-x-3 font-bold text-earth">
              <ElementIcon className="h-3 w-3" />
              <span className="inline-block pt-0.75">Cloud Storage</span>
            </h3>
            <PresetsPanel
              presets={presets}
              activePreset={activePreset}
              mode={mode}
              onPresetSelected={onPresetSelected}
              onDeletePresetClicked={onDeletePresetClicked}
              onRenamePresetClicked={onRenamePresetClicked}
              onSearchTextChanged={onSearchTextChanged}
              className="mt-2"
              activePresetOutdated={activePresetOutdated}
              isPresetLoading={isPresetLoading}
              onSavePresetClicked={onSavePresetClicked}
              createPreset={createPreset}
            />
          </div>
        ) : (
          <div className="mb-4">
            <LoggedOutWarning />
          </div>
        )}
        <Button
          theme="blue"
          size="xs"
          onClick={() => setIsSharedPresetsModalOpen(true)}
          className="w-full"
        >
          Browse Community Presets
        </Button>

        <SharedPresetsModal
          isOpen={isSharedPresetsModalOpen}
          onClose={() => setIsSharedPresetsModalOpen(false)}
          onPresetSelected={onPresetSelected}
          createPreset={createPreset}
        />
      </section>
    </>
  )
}

const ModePanelHeader = ({ mode, onModeChanged, deviceSettings }) => {
  const { updateSettings, status: deviceStatus } = useSerialDevice()
  const [currentInputMode, setCurrentInputMode] = useState(
    deviceSettings?.Configuration?.General?.["Input Mode"]?.Current || "USB"
  )

  // Update currentInputMode when deviceSettings changes
  useEffect(() => {
    const newInputMode = deviceSettings?.Configuration?.General?.["Input Mode"]?.Current
    if (newInputMode) {
      setCurrentInputMode(newInputMode)
    }
  }, [deviceSettings])

  const onInputModeChanged = async (inputMode) => {
    if (deviceStatus === DEVICE_CONNECTED) {
      try {
        await updateSettings({
          Product: "JDS Labs Element IV",
          "Format Output": true,
          Action: "Update",
          Configuration: {
            General: {
              "Input Mode": inputMode,
            },
          },
        })
        // Update local state after successful device update
        setCurrentInputMode(inputMode)
      } catch (error) {
        console.error("Error updating input mode:", error)
        toast.error("Error updating input mode")
      }
    }
  }

  return (
    <header>
      {/* Output Mode */}
      <h3 className="type-body-sm font-bold">Output Mode</h3>
      <div className="mt-4">
        <div className="flex">
          <button
            className={cx(
              "type-body-xs clickable flex flex-1 items-center pb-3.5 pl-2 font-medium",
              {
                "bg-gradient-to-b from-dark to-cedar": mode === MODE_HEADPHONES,
              }
            )}
            onClick={() => {
              onModeChanged(MODE_HEADPHONES)
            }}
          >
            <HeadphoneIcon
              className={cx("mr-3 w-[15px]", { "text-red": mode === MODE_HEADPHONES })}
            />
            {MODE_HEADPHONES}
          </button>
          <button
            className={cx(
              "type-body-xs clickable flex flex-1 items-center pb-3.5 pl-2 font-medium",
              {
                "bg-gradient-to-b from-dark to-cedar": mode === MODE_RCA,
              }
            )}
            onClick={() => {
              onModeChanged(MODE_RCA)
            }}
          >
            <RCAIcon className={cx("mr-3 w-5", { "text-red": mode === MODE_RCA })} />
            {MODE_RCA}
          </button>
        </div>
        <div className="relative h-0.75 w-full overflow-hidden bg-tundora">
          <div
            className={cx("absolute top-0 h-full w-1/2 bg-red transition-all ease-out", {
              "left-0": mode === MODE_HEADPHONES,
              "left-1/2": mode === MODE_RCA,
            })}
          ></div>
        </div>
      </div>
      {/* Input Mode */}
      <div className="mt-8">
        <h3 className="type-body-sm font-bold">Input Mode</h3>
        <div className="mt-2.5">
          <div className="flex">
            <button
              className={cx(
                "type-body-xs clickable flex flex-1 items-center pb-2 pl-2 font-medium",
                {
                  "bg-gradient-to-b from-dark to-cedar": currentInputMode === "USB",
                }
              )}
              onClick={() => onInputModeChanged("USB")}
            >
              <USBIcon className={cx("mr-3 w-4.5", { "text-red": currentInputMode === "USB" })} />
              USB
            </button>
            <button
              className={cx(
                "type-body-xs clickable flex flex-1 items-center pb-2 pl-2 font-medium",
                {
                  "bg-gradient-to-b from-dark to-cedar": currentInputMode === "SPDIF",
                }
              )}
              onClick={() => onInputModeChanged("SPDIF")}
            >
              <TOSLINKIcon
                className={cx("mr-2 w-5", { "text-red": currentInputMode === "SPDIF" })}
              />
              {deviceSettings?.Configuration?.General?.["SPDIF mode"]?.Current === "ESS S/PDIF"
                ? "S/PDIF (No DSP)"
                : "S/PDIF"}
            </button>
          </div>
          <div className="relative h-0.75 w-full overflow-hidden bg-tundora">
            <div
              className={cx("absolute top-0 h-full w-1/2 bg-red transition-all ease-out", {
                "left-0": currentInputMode === "USB",
                "left-1/2": currentInputMode === "SPDIF",
              })}
            ></div>
          </div>
        </div>
      </div>
      {/* My EQ Presets */}
      <h4 className="type-body-sm mt-8 font-bold">EQ Presets</h4>
    </header>
  )
}

const SortMenu = ({ sortBy, onSortChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        className="type-body-xs flex items-center gap-x-1 text-silver hover:text-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <SearchIcon className="h-3.5 w-3.5" />
        Sort
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full z-10 mt-1 w-48 rounded border border-jaguar bg-dark-jaguar py-1">
          <button
            className={cx(
              "type-body-xs w-full px-3 py-1.5 text-left hover:bg-[#2A2A2A]",
              sortBy === "name" && "text-red"
            )}
            onClick={() => {
              onSortChange("name")
              setIsOpen(false)
            }}
          >
            Sort by Name
          </button>
          <button
            className={cx(
              "type-body-xs w-full px-3 py-1.5 text-left hover:bg-[#2A2A2A]",
              sortBy === "updated_at" && "text-red"
            )}
            onClick={() => {
              onSortChange("updated_at")
              setIsOpen(false)
            }}
          >
            Sort by Last Updated
          </button>
        </div>
      )}
    </div>
  )
}

const PresetsPanel = ({
  presets,
  activePreset,
  mode,
  onPresetSelected,
  onDeletePresetClicked,
  onRenamePresetClicked,
  onSearchTextChanged,
  className,
  activePresetOutdated,
  isPresetLoading,
  onSavePresetClicked,
  createPreset,
}) => {
  const [localPresets, setLocalPresets] = useState(presets)
  const [sortBy, setSortBy] = useState("name")

  // Update local presets when props change
  useEffect(() => {
    setLocalPresets(presets)
  }, [presets])

  const modePresets = localPresets
    ? localPresets
        .filter((preset) => preset.mode.toLowerCase() === mode.toLowerCase())
        .sort((a, b) => {
          if (sortBy === "name") {
            return a.name.localeCompare(b.name)
          } else {
            const dateA = new Date(a.updated_at).getTime()
            const dateB = new Date(b.updated_at).getTime()
            return dateB - dateA
          }
        })
    : []

  if (!localPresets) {
    return <></>
  }

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-start">
        <SortMenu sortBy={sortBy} onSortChange={setSortBy} />
      </div>
      <ul className="space-y-1.5">
        {modePresets.length === 0 && (
          <p className="text-sm font-normal text-silver">No presets saved</p>
        )}
        {modePresets.map((preset, index) => {
          return (
            <li key={index}>
              <button
                className={cx(
                  "type-body-xs flex h-8 w-full items-center justify-between rounded-plus border pl-3 pr-2 font-medium transition-colors duration-200",
                  activePreset && preset.id === activePreset.id
                    ? "border-[#4B5E47] bg-[#384B32]"
                    : "border-jaguar bg-dark-jaguar hover:border-silver"
                )}
                onClick={() => onPresetSelected(preset)}
              >
                <div className="flex items-center">
                  {preset.is_favourite && (
                    <StarIcon className="mr-2.5 h-3 w-3 [&>path]:fill-yellow [&>path]:stroke-none" />
                  )}
                  <span
                    className={cx(
                      "truncate",
                      activePreset && preset.id === activePreset.id
                        ? "max-w-[120px]"
                        : "max-w-[180px]"
                    )}
                  >
                    {preset.name}
                  </span>
                </div>
                <div className="flex items-center gap-x-3">
                  {activePreset && preset.id === activePreset.id && isPresetLoading && (
                    <div className="pt-px font-pfdin text-[8px] font-medium uppercase tracking-widest2x text-[#71A96C]">
                      Loading...
                    </div>
                  )}
                  {activePreset &&
                    preset.id === activePreset.id &&
                    !isPresetLoading &&
                    !activePresetOutdated && (
                      <div className="pt-px font-pfdin text-[8px] font-medium uppercase tracking-widest2x text-[#71A96C]">
                        Active
                      </div>
                    )}
                  {activePreset &&
                    preset.id === activePreset.id &&
                    !isPresetLoading &&
                    activePresetOutdated && (
                      <div className="pt-px font-pfdin text-[8px] font-medium uppercase tracking-widest2x text-[#71A96C]">
                        Active - Unsaved
                      </div>
                    )}
                  <PresetOptionsMenu
                    preset={preset}
                    activePreset={activePreset}
                    activePresetOutdated={activePresetOutdated}
                    onDeletePresetClicked={onDeletePresetClicked}
                    onRenamePresetClicked={onRenamePresetClicked}
                    onSavePresetClicked={onSavePresetClicked}
                  />
                </div>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const DevicePresetsPanel = ({
  onPresetSelected,
  presets,
  currentEqState,
  deviceProfiles,
  activeDeviceProfile,
  onSaveToDevice,
  onDeleteDeviceProfile,
  onActivateDeviceProfile,
  mode,
  activePreset,
}) => {
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const TOTAL_SLOTS = 10

  // Map our mode format to device format (e.g. "HEADPHONES" -> "Headphone")
  const deviceModeMap = {
    [MODE_HEADPHONES]: "Headphone",
    [MODE_RCA]: "RCA",
  }
  const currentDeviceMode = deviceModeMap[mode]

  // Calculate remaining slots across all modes
  const remainingSlots = TOTAL_SLOTS - (deviceProfiles?.length || 0)

  // Filter profiles by current mode and format for display
  const formattedDeviceProfiles =
    deviceProfiles
      ?.filter((profile) => profile.Type === currentDeviceMode)
      .map((profile) => ({
        id: profile.ID,
        name: profile.Name,
        type: profile.Type,
        isActive: profile.Active,
      })) || []

  // Add the "Add New" option if there are remaining slots
  if (remainingSlots > 0) {
    formattedDeviceProfiles.push({
      id: -1,
      name: `Add New (${remainingSlots} Free Slots)`,
      isNew: true,
    })
  }

  const handleSaveToDevice = (name) => {
    onSaveToDevice(name)
    setIsSaveModalOpen(false)
  }

  const handleDeleteProfile = (profileId) => {
    if (window.confirm("Are you sure you want to delete this profile from the device?")) {
      onDeleteDeviceProfile(profileId)
    }
  }

  const handleActivateProfile = (profileId) => {
    onActivateDeviceProfile(profileId)
  }

  return (
    <div className="mb-6">
      <h3 className="type-heading-xs flex items-center gap-x-3 font-bold text-earth">
        <ElementIcon className="h-3 w-3" />
        <span className="inline-block pt-0.75">Element IV Storage</span>
      </h3>
      <ul className="mt-2 space-y-1.5">
        {formattedDeviceProfiles.map((preset, index) => (
          <li key={index}>
            <button
              className={cx(
                "type-body-xs flex h-8 w-full items-center justify-between rounded-plus border pl-3 pr-2 font-medium transition-colors duration-200",
                preset.isNew
                  ? "border-[#4A4A4A] bg-[#3D3D3D] text-silver"
                  : preset.isActive
                    ? "border-[#4B5E47] bg-[#384B32]"
                    : "border-jaguar bg-dark-jaguar hover:border-silver"
              )}
              onClick={() => {
                if (preset.isNew) {
                  setIsSaveModalOpen(true)
                } else {
                  handleActivateProfile(preset.id)
                }
              }}
            >
              <div className="flex items-center">{preset.name}</div>
              <div className="flex items-center gap-x-3">
                {preset.isActive && (
                  <span className="pt-px font-pfdin text-[8px] font-medium uppercase tracking-widest2x text-[#71A96C]">
                    Active
                  </span>
                )}
                {preset.isNew ? (
                  <CirclePlusIcon className="h-3.5 w-3.5 text-[#858484]" />
                ) : (
                  <TrashIcon
                    className="h-3.5 w-3.5 text-[#858484] hover:text-red"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteProfile(preset.id)
                    }}
                  />
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>

      <SaveToDeviceModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onConfirm={handleSaveToDevice}
        presets={presets}
        currentEqState={currentEqState}
        mode={mode}
        onPresetSelected={onPresetSelected}
        activePreset={activePreset}
      />
    </div>
  )
}

const LoggedOutWarning = () => {
  const navigate = useNavigate()
  return (
    <WarningCallout
      icon={<ElementIcon className="h-12" />}
      heading={<h2>Sign in for Core Storage</h2>}
      content={
        <>
          <p>
            Sign in to save and share unlimited EQ presets through Core storage. You may store up to
            10 presets directly on your Element IV.
          </p>
          <p>
            <Button
              theme="blue"
              size="xs"
              outline
              onClick={() => {
                navigate("/login")
              }}
            >
              Login
            </Button>
          </p>
        </>
      }
    ></WarningCallout>
  )
}

export default ModePanel
